import { useEffect, useRef } from "react";

/**
 * Adds `.pg-reveal-in` to the returned ref's element the first time it enters
 * the viewport. Used together with the `.pg-reveal` base class in index.css
 * to fade + slide content up gently on scroll. Runs once per element.
 *
 * Usage:
 *   const ref = useReveal();
 *   <section ref={ref} className="pg-reveal">...</section>
 */
export default function useReveal(options = { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // If the browser has no IntersectionObserver (very old), reveal immediately.
    if (typeof IntersectionObserver === "undefined") {
      el.classList.add("pg-reveal-in");
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("pg-reveal-in");
            observer.unobserve(entry.target);
          }
        });
      },
      options
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}
